import { useState, useRef } from "react";
import { Upload, Link2, Star, Trash2, Loader2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import api, { formatApiError } from "../../lib/api";
import { cn } from "../../lib/utils";

export default function MediaUploader({ value = [], onChange }) {
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const update = (next) => {
    // Guarantee exactly one primary.
    if (next.length && !next.some((m) => m.is_primary)) next[0].is_primary = true;
    onChange(next);
  };

  const addUrl = () => {
    if (!url.trim()) return;
    update([...value, { url: url.trim(), alt: "", is_primary: value.length === 0 }]);
    setUrl("");
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      update([...value, {
        media_id: data.media_id, url: data.url, webp_url: data.webp_url,
        thumb_url: data.thumb_url, alt: data.alt || "", is_primary: value.length === 0,
      }]);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = (i) => update(value.filter((_, idx) => idx !== i));
  const setPrimary = (i) => update(value.map((m, idx) => ({ ...m, is_primary: idx === i })));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    update(next);
  };

  return (
    <div className="space-y-4" data-testid="media-uploader">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 flex gap-2">
          <Input
            data-testid="media-url-input"
            placeholder="Paste an image URL…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="bg-black/30 border-white/15 text-white"
          />
          <Button type="button" variant="outline" data-testid="media-add-url-btn" onClick={addUrl} className="border-white/20 text-white gap-2">
            <Link2 className="h-4 w-4" /> Add
          </Button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUpload} data-testid="media-file-input" />
        <Button type="button" data-testid="media-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()} className="cf-gold-btn font-semibold gap-2">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
        </Button>
      </div>

      {value.length === 0 ? (
        <p className="text-sm text-muted-foreground">No images yet. Upload files or add image URLs. The first image is the primary.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {value.map((m, i) => (
            <div key={i} data-testid={`media-item-${i}`} className={cn("relative rounded-xl overflow-hidden border group", m.is_primary ? "border-gold" : "border-white/15")}>
              <img src={m.thumb_url || m.url} alt={m.alt} className="h-28 w-full object-cover" />
              {m.is_primary && <span className="absolute top-1.5 left-1.5 text-[10px] font-bold bg-gold text-ink px-1.5 py-0.5 rounded">PRIMARY</span>}
              <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-black/70 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button type="button" title="Set primary" data-testid={`media-primary-${i}`} onClick={() => setPrimary(i)} className="text-white/80 hover:text-gold p-1"><Star className="h-3.5 w-3.5" /></button>
                <button type="button" title="Move left" onClick={() => move(i, -1)} className="text-white/80 hover:text-white p-1"><ArrowUp className="h-3.5 w-3.5 -rotate-90" /></button>
                <button type="button" title="Move right" onClick={() => move(i, 1)} className="text-white/80 hover:text-white p-1"><ArrowDown className="h-3.5 w-3.5 -rotate-90" /></button>
                <button type="button" title="Remove" data-testid={`media-remove-${i}`} onClick={() => remove(i)} className="text-white/80 hover:text-firered p-1"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
